import React, { useEffect, useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';
import some from 'lodash/some';

import analytics from '@ad-scripts/lib/analytics';
import CustomChannelEditor from './CustomChannelEditor';
import EmptyState from 'src/presentation/components/pages/proactiveCommunications/components/shared/EmptyState';
import { isLoadingStatus } from 'src/presentation/hooks/useResource';
import OverflowMenu, {
  OverflowMenuItem,
  OverflowMenuLink,
  usePopoverMenu,
} from 'src/presentation/components/pages/shared/OverflowMenu';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import { StyledConfirmOrCancelModal } from 'src/presentation/components/pages/shared/rowActions/StyledConfirmOrCancelModal';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import useCustomChannel from 'src/presentation/components/pages/channels/customChannels/hooks/useCustomChannel';
import useToggleArchive from 'src/presentation/components/pages/channels/customChannels/hooks/useToggleArchive';

const columnConfig = [
  { attr: 'name', label: 'Name', sorting: true, customColumnStyle: () => ({ width: '50%' }) },
  { attr: 'updatedByString', label: 'Last Edited By' },
  { attr: 'updatedAtString', label: 'Last Edited On' },
];

const tabConfig = [
  { label: 'Active', filter: customChannel => !customChannel.archived, key: `custom-channels-tab-enabled` },
  { label: 'Archived', filter: customChannel => customChannel.archived, key: `custom-channels-tab-disabled` },
];

const sortingConfig = { attr: 'name' };

const CustomChannels: TablePageRender = ({ root }) => {
  const { customChannels, loadingState, resetErrors, errors } = useCustomChannel();

  const [currentTabConfig, setCurrentTabConfig] = useState(tabConfig[0]);

  const { archive, closeArchiveModal, customChannel, isArchiveModalVisible, openArchiveModal, unarchive } =
    useToggleArchive();

  useEffect(() => {
    const items = filter(customChannels, currentTabConfig.filter);

    if (customChannels.length && !items.length && currentTabConfig.label === 'Archived') {
      setCurrentTabConfig(tabConfig[0]);
    }
    if (customChannels.length && !items.length && currentTabConfig.label === 'Active') {
      setCurrentTabConfig(tabConfig[1]);
    }
  }, [customChannels, currentTabConfig, setCurrentTabConfig]);

  const renderRowActions = ({ item }) => {
    const toggleArchiveHandler = item.archived ? unarchive : openArchiveModal;
    return <RowActions item={item} root={root} toggleArchiveHandler={toggleArchiveHandler} />;
  };

  const actionsRender = (
    <RouterLinkButton data-aid="customChannel-addNew-button" to={`${root}/new`}>
      Add Channel
    </RouterLinkButton>
  );

  const description = <span>Configure and manage your Custom Channels.</span>;

  const emptyStateRender = <EmptyState title="Add your first Custom Channel" actionsRender={actionsRender} />;

  return (
    <>
      <TablePage
        actions={actionsRender}
        columnConfig={columnConfig}
        data-aid="custom-channel-page"
        description={description}
        editor={CustomChannelEditor}
        emptyStateFullHeightLayout
        emptyStateRender={emptyStateRender}
        errors={errors}
        filterFunc={filterFunc}
        initialSorting={sortingConfig}
        isLoading={isLoadingStatus(loadingState)}
        items={customChannels}
        name="Custom Channels"
        onTabChange={setCurrentTabConfig}
        renderRowActions={renderRowActions}
        resetErrors={resetErrors}
        preselectedTab={currentTabConfig.key}
        root={root}
        tabConfig={tabConfig}
      />
      {isArchiveModalVisible ? (
        <StyledConfirmOrCancelModal
          confirmationText="You sure about that?"
          id={customChannel?.id}
          onCancel={closeArchiveModal}
          onSubmit={() => archive(customChannel)}
          submitText="Yes, archive channel"
          text={
            <>
              Are you sure you want to archive this channel?
              <br /> If so, it will no longer appear in the Customer Profile and
              <br /> Plus Menu.
            </>
          }
          title={`Archive "${customChannel?.name}"`}
        />
      ) : null}
    </>
  );
};

const filterFunc = (items, searchString) => {
  const searchPaths = ['name'];
  if (searchString) {
    const search = searchString.toLowerCase().trim();
    return filter(items, item =>
      some(searchPaths, columnKey => get(item, columnKey, '').toLowerCase().includes(search))
    );
  }
  return items;
};

const RowActions = ({ root, item, toggleArchiveHandler }) => {
  const { onClose, ...popoverMenuProps } = usePopoverMenu();

  const isArchived = item.archived;
  const archiveMenuLabel = isArchived ? 'Unarchive Channel' : 'Archive Channel';

  const archiveHandle = () => {
    analytics.track(`Admin - Custom Channels - ${archiveMenuLabel}`, {
      customChannelId: item.address,
    });
    onClose();
    toggleArchiveHandler(item);
  };

  return (
    <OverflowMenu id={item.id} onClose={onClose} {...popoverMenuProps}>
      <OverflowMenuItem id="editRecipientIds">
        <OverflowMenuLink to={`${root}/${item.id}/recipients`}>Edit Entry Points</OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem id="editChannel">
        <OverflowMenuLink to={`${root}/${item.id}`}>Edit Channel</OverflowMenuLink>
      </OverflowMenuItem>
      <OverflowMenuItem id="archiveChannel">
        <OverflowMenuLink onClick={() => archiveHandle()} to="#">
          {archiveMenuLabel}
        </OverflowMenuLink>
      </OverflowMenuItem>
    </OverflowMenu>
  );
};

export default CustomChannels;
